import React, { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { BaseFormFields, useBaseForm } from "./BaseFormFields";

const CHANGE_TYPES = [
    { id: "standard", label: "Standard", icon: "bg-blue-400", desc: "Procédure habituelle" },
    { id: "normal", label: "Normal", icon: "bg-green-400", desc: "Requiert approbation" },
    { id: "emergency", label: "Urgence", icon: "bg-red-500", desc: "Intervention immédiate" },
];

const RISK_LEVELS = [
    { id: "low", label: "Faible", icon: "bg-green-400" },
    { id: "medium", label: "Moyen", icon: "bg-yellow-400" },
    { id: "high", label: "Élevé", icon: "bg-red-500" },
];

export function ChangeForm({ onSubmit, initialData, readOnly, submitting, onTitleChange, allowedPriorities, ticketType, onTypeChange }) {
    const baseForm = useBaseForm("medium");
    const [changeType, setChangeType] = useState("normal");
    const [plannedStart, setPlannedStart] = useState("");
    const [plannedEnd, setPlannedEnd] = useState("");
    const [rollbackPlan, setRollbackPlan] = useState("");
    const [affectedSystems, setAffectedSystems] = useState("");
    const [riskLevel, setRiskLevel] = useState("medium");
    const [testingDone, setTestingDone] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            title: baseForm.title,
            description: baseForm.description,
            priority: baseForm.priority,
            dynamic_fields: {
                change_type: changeType,
                planned_start: plannedStart,
                planned_end: plannedEnd,
                rollback_plan: rollbackPlan,
                affected_systems: affectedSystems.split(",").map((s) => s.trim()).filter(Boolean),
                risk_level: riskLevel,
                testing_done: testingDone,
            },
        });
    };

    return (
        <form onSubmit={handleSubmit}>
            <BaseFormFields
                ticketType={ticketType}
                onTypeChange={onTypeChange}
                {...baseForm}
                onTitleChange={onTitleChange}
                    allowedPriorities={allowedPriorities}
                titlePlaceholder="Ex: Mise à jour serveur de fichiers"
                descriptionPlaceholder="Décrivez le changement demandé..."
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                        <Label className="form-label">Type de changement</Label>
                        <Select value={changeType} onValueChange={setChangeType}>
                            <SelectTrigger data-testid="change-type">
                                <SelectValue>
                                    {(() => { const t = CHANGE_TYPES.find((t) => t.id === changeType); return t ? <span className="flex items-center gap-2.5"><span className={`w-2.5 h-2.5 rounded-full ${t.icon}`} />{t.label}</span> : null; })()}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {CHANGE_TYPES.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>
                                        <span className="flex items-center gap-2.5">
                                            <span className={`w-2.5 h-2.5 rounded-full ${t.icon}`} />
                                            <span>{t.label}</span>
                                            <span className="text-[10px] text-gray-400 ml-auto">{t.desc}</span>
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="form-group">
                        <Label className="form-label">Niveau de risque</Label>
                        <Select value={riskLevel} onValueChange={setRiskLevel}>
                            <SelectTrigger data-testid="risk-level">
                                <SelectValue>
                                    {(() => { const r = RISK_LEVELS.find((r) => r.id === riskLevel); return r ? <span className="flex items-center gap-2.5"><span className={`w-2.5 h-2.5 rounded-full ${r.icon}`} />{r.label}</span> : null; })()}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {RISK_LEVELS.map((r) => (
                                    <SelectItem key={r.id} value={r.id}>
                                        <span className="flex items-center gap-2.5">
                                            <span className={`w-2.5 h-2.5 rounded-full ${r.icon}`} />
                                            {r.label}
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                        <Label className="form-label">Date/heure de début prévue</Label>
                        <Input
                            type="datetime-local"
                            value={plannedStart}
                            onChange={(e) => setPlannedStart(e.target.value)}
                            data-testid="planned-start"
                        />
                    </div>

                    <div className="form-group">
                        <Label className="form-label">Date/heure de fin prévue</Label>
                        <Input
                            type="datetime-local"
                            value={plannedEnd}
                            onChange={(e) => setPlannedEnd(e.target.value)}
                            data-testid="planned-end"
                        />
                    </div>
                </div>

                <div className="form-group">
                    <Label className="form-label">Systèmes affectés</Label>
                    <Input
                        value={affectedSystems}
                        onChange={(e) => setAffectedSystems(e.target.value)}
                        placeholder="Séparez par des virgules"
                        data-testid="affected-systems"
                    />
                </div>

                <div className="form-group">
                    <Label className="form-label">Plan de retour arrière</Label>
                    <Textarea
                        value={rollbackPlan}
                        onChange={(e) => setRollbackPlan(e.target.value)}
                        placeholder="Comment annuler ce changement si nécessaire ?"
                        data-testid="rollback-plan"
                    />
                </div>

                <div className="form-group">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="testing"
                            checked={testingDone}
                            onCheckedChange={setTestingDone}
                            data-testid="testing-done"
                        />
                        <Label htmlFor="testing" className="text-sm font-medium cursor-pointer">
                            Tests effectués en environnement de pré-production
                        </Label>
                    </div>
                </div>
            </BaseFormFields>

            <div className="mt-6 flex justify-end">
                <Button type="submit" disabled={!baseForm.title || submitting} data-testid="submit-ticket">
                    {submitting ? "Création..." : "Soumettre le ticket"}
                </Button>
            </div>
        </form>
    );
}
